<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-emploi-du-temps-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form
        ref="emploiDuTempsForm"
        @submit="addEmploiDuTemps"
        @invalid-submit="handleInvalidSubmit"
        :validation-schema="emploiDuTempsSchema"
        :validateOnInput="false"
        :validateOnMount="false"
        :initialValues="{
          typeEmploi: '',
          dateDebut: getCurrentDate(),
          dateFin: getCurrentDate(),
          niveau: '',
          filiere: '',
          coursAdded: []
        }"
      >
        <!-- Informations générales sur une ligne -->
        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Type d'emploi du temps <span class="text-danger">*</span>
              </label>
              <Field name="typeEmploi" v-slot="{ field, handleChange }">
                <Multiselect
                  :options="typeEmploiOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  :modelValue="field.value"
                  @update:modelValue="(val) => { handleChange(val); onTypeEmploiChange(val); }"
                  placeholder="Sélectionner le type"
                />
              </Field>
              <ErrorMessage name="typeEmploi" class="text-danger"/>
            </div>
          </div>
          
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date début <span class="text-danger">*</span>
              </label>
              <Field name="dateDebut" v-slot="{ field }">
                <input
                  v-bind="field"
                  type="date"
                  :min="getCurrentDate()"
                  class="form-control shadow-none fs-md-15 text-black"
                  @change="onDateDebutChange"
                />
              </Field>
              <ErrorMessage name="dateDebut" class="text-danger"/>
            </div>
          </div>
          
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date fin <span class="text-danger">*</span>
              </label>
              <Field name="dateFin" v-slot="{ field }">
                <input
                  v-bind="field"
                  type="date"
                  class="form-control shadow-none fs-md-15 text-black bg-light"
                  readonly
                  disabled
                  :title="`Date calculée automatiquement selon le type ${selectedTypeEmploiLibelle}`"
                />
              </Field>
              <ErrorMessage name="dateFin" class="text-danger"/>
              
              <small v-if="dateDebut && dateFin && selectedTypeEmploiLibelle" class="text-muted d-block mt-1">
                <i class="fas fa-info-circle me-1"></i>
                Période de {{ getDiffJours(dateDebut, dateFin) }} jours ({{ selectedTypeEmploiLibelle }})
              </small>
            </div>
          </div>
        </div>

        <!-- Niveau en premier, puis Filière -->
        <div class="row">
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Niveau <span class="text-danger">*</span>
              </label>
              <Field name="niveau" v-slot="{ field }">
                <Multiselect
                  :options="globalNiveauxOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onGlobalNiveauChange"
                  placeholder="Sélectionner le niveau"
                />
              </Field>
              <ErrorMessage name="niveau" class="text-danger"/>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Filière <span class="text-danger">*</span>
              </label>
              <Field name="filiere" v-slot="{ field }">
                <Multiselect
                  :options="globalFilieresOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onGlobalFiliereChange"
                  placeholder="Sélectionner la filière"
                  :disabled="!globalSelectedNiveau || isLoadingFilieres"
                />
              </Field>
              <ErrorMessage name="filiere" class="text-danger"/>
            </div>
          </div>
        </div>

        <!-- Section Cours -->
        <div class="row">
          <div class="col-md-12">
            <div class="border border-primary rounded-3 mt-4 mb-3">
              <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-primary rounded-0">
                <div class="col-md-7">
                  <h4 class="fs-5 text-white">Ajouter des cours</h4>
                </div>
                <div class="col-md-5">
                  <div class="d-flex justify-content-end"> 
                    <button
                      class="btn btn-success fw-semibold shadow-sm text-nowrap"
                      type="button"
                      @click="addNewCours"
                      :disabled="!isLastCoursComplete"
                      :title="!isLastCoursComplete
                        ? 'Veuillez remplir tous les champs obligatoires du cours actuel'
                        : 'Ajouter un nouveau cours'"
                    >
                      <i class="fa fa-plus-circle me-2 fs-6"></i>
                      Ajouter un cours
                    </button>
                  </div>
                </div>
              </div>

              <!-- Liste dynamique des cours -->
              <div class="marge-droite">
                <div v-for="(cours, index) in addedCours" :key="cours.tempId">
                  <!-- Badge pour séparer chaque cours -->
                  <div class="row mt-10">
                    <div class="col-md-12">
                      <h5 class="badge bg-primary text-white fs-6 mb-10">
                        Cours {{ index + 1 }}
                      </h5>
                    </div>
                  </div>
                  
                  <!-- Première ligne : Jour, Heure début, Heure fin, Matière -->
                  <div class="row mt-10">
                    <div class="col-md-3">
                      <label class="d-block text-black fw-semibold mb-10">
                        Jour <span class="text-danger">*</span>
                      </label>
                      <Field :name="`coursAdded[${index}].jour`" v-slot="{ value, handleChange }">
                        <Multiselect
                          :options="joursOptions"
                          :searchable="true"
                          track-by="value"
                          label="label"
                          :modelValue="value"
                          @update:modelValue="(val) => {
                            handleChange(val);
                            onJourChange(index, val);
                            if (addedCours[index]) {
                              addedCours[index].jour = val?.value || val;
                            }
                            checkCoursAvailability(index);
                          }"
                          placeholder="Sélectionner un jour"
                        />
                      </Field>
                      <ErrorMessage :name="`coursAdded[${index}].jour`" class="text-danger"/>
                    </div>

                    <div class="col-md-2">
                      <label class="d-block text-black fw-semibold mb-10">
                        Heure de début <span class="text-danger">*</span>
                      </label>
                      <Field :name="`coursAdded[${index}].heureDebut`" type="time"
                        v-slot="{ field }"
                        class="form-control shadow-none text-black">
                        <input
                          v-bind="field"
                          type="time"
                          class="form-control shadow-none text-black"
                          @change="(event) => onHeureChange(index, 'debut', event)"
                        />
                      </Field>
                      <ErrorMessage :name="`coursAdded[${index}].heureDebut`" class="text-danger"/>
                    </div>

                    <div class="col-md-2">
                      <label class="d-block text-black fw-semibold mb-10">
                        Heure de fin <span class="text-danger">*</span>
                      </label>
                      <Field :name="`coursAdded[${index}].heureFin`" type="time"
                        v-slot="{ field }"
                        class="form-control shadow-none text-black">
                        <input
                          v-bind="field"
                          type="time"
                          class="form-control shadow-none text-black"
                          @change="(event) => onHeureChange(index, 'fin', event)"
                        />
                      </Field>
                      <ErrorMessage :name="`coursAdded[${index}].heureFin`" class="text-danger"/>
                    </div>

                    <div class="col-md-5">
                      <label class="d-block text-black fw-semibold mb-10">
                        Matière <span class="text-danger">*</span>
                      </label>
                      <Field :name="`coursAdded[${index}].filiereNiveauMatiere`" v-slot="{ field, value }">
                        <Multiselect
                          :options="globalMatieresOptions"
                          :searchable="true"
                          track-by="value"
                          label="label"
                          v-bind="field"
                          :value="value"
                          @change="(matiereValue) => onMatiereChange(index, matiereValue)"
                          @input="(matiereValue) => { 
                            if (addedCours[index]) {
                              addedCours[index].filiereNiveauMatiere = matiereValue?.value || matiereValue || null;
                            }
                          }"
                          placeholder="Sélectionner la matière"
                          :disabled="!globalSelectedNiveau || !globalSelectedFiliere || isLoadingMatieres"
                        />
                      </Field>
                      <ErrorMessage :name="`coursAdded[${index}].filiereNiveauMatiere`" class="text-danger"/>
                    </div>
                  </div>

                  <!-- Deuxième ligne : Professeur, Classe, En ligne/Présentiel, Lien visio, Supprimer -->
                <div class="row mt-10">
                  <div class="col-md-4">
                    <label class="d-block text-black fw-semibold mb-10">
                      Professeur <span class="text-danger">*</span>
                    </label>
                    <Field :name="`coursAdded[${index}].professeur`" v-slot="{ value, handleChange }">
                      <Multiselect
                        :options="getProfesseursByMatiere(cours.matiereId)"
                        :searchable="true"
                        track-by="label"
                        label="label"
                        :modelValue="value"
                        @update:modelValue="(val) => {
                          handleChange(val);
                          if (addedCours[index]) {
                            addedCours[index].professeur = val?.value || val || null;
                          }
                          checkCoursAvailability(index);
                        }"
                        placeholder="Sélectionner le professeur"
                        :disabled="!cours.matiereId || !getProfesseursByMatiere(cours.matiereId).length"
                      />
                    </Field>
                    <ErrorMessage :name="`coursAdded[${index}].professeur`" class="text-danger" />
                  </div>

                  <div class="col-md-3">
                    <label class="d-block text-black fw-semibold mb-10">
                      Classe <span class="text-danger">*</span>
                    </label>
                    <Field :name="`coursAdded[${index}].classe`" v-slot="{ value, handleChange }">
                      <Multiselect
                        :options="classesOptions"
                        :searchable="true"
                        track-by="label"
                        label="label"
                        :modelValue="value"
                        @update:modelValue="(val) => {
                          handleChange(val);
                          onClasseChange(index, val);
                          if (addedCours[index]) {
                            addedCours[index].classe = val?.value || val || null;
                          }
                          checkCoursAvailability(index);
                        }"
                        placeholder="Sélectionner la classe"
                      />
                    </Field>
                    <ErrorMessage :name="`coursAdded[${index}].classe`" class="text-danger"/>
                  </div>

                  <div class="col-md-2 d-flex align-items-center mt-30">
                    <div class="form-check">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :id="`enligne-${index}`"
                        v-model="addedCours[index].estEnLigne"
                        @change="handleCheckboxChange(index, $event)"
                      />
                      <label class="form-check-label fw-semibold" :for="`enligne-${index}`">
                        En ligne
                      </label>
                    </div>
                  </div>

                  <div class="col-md-4" v-if="addedCours[index].estEnLigne">
  <label class="d-block text-black fw-semibold mb-10">
    Lien Visioconférence <span class="text-danger">*</span>
  </label>

  <Field :name="`coursAdded[${index}].lienVisio`" v-slot="{ field }">
    <input
      v-bind="field"
      type="url"
      class="form-control shadow-none text-black"
      placeholder="https://meet.google.com/xxx-xxxx-xxx"
      @input="onVisioInput($event, index)"
    />
  </Field>

  <ErrorMessage :name="`coursAdded[${index}].lienVisio`" class="text-danger"/>
</div>


                  <div class="col-md-2 d-flex align-items-start" :class="{ 'mt-30': !addedCours[index].estEnLigne }">
                    <button
                      class="btn btn-danger transition border-0 ps-8 pe-8"
                      type="button"
                      @click="removeCours(index)"
                      v-if="addedCours.length > 0"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>

                  <!-- ===== SECTION ALERTES UNIFIÉE ===== -->
                  <div 
                    v-if="coursDisponibilite[cours.tempId] && !coursDisponibilite[cours.tempId].available" 
                    class="row mt-3"
                  >
                    <div class="col-md-12">
                      <div class="alert alert-danger d-flex align-items-start mb-0" role="alert">
                        <i class="fas fa-exclamation-triangle me-2 mt-1"></i>
                        <div class="flex-grow-1">
                          <!-- Conflit de classe -->
                          <div v-if="coursDisponibilite[cours.tempId].conflictingCours?.conflictType === 'classe'">
                            <strong>Classe occupée :</strong>
                            La classe <strong>{{ getClasseLabel(cours.classe || null) }}</strong> est déjà occupée 
                            le <strong>{{ coursDisponibilite[cours.tempId].conflictingCours.details.jour }}</strong>
                            de <strong>{{ coursDisponibilite[cours.tempId].conflictingCours.details.heureDebut }}</strong>
                            à <strong>{{ coursDisponibilite[cours.tempId].conflictingCours.details.heureFin }}</strong>
                            
                          </div>
                          
                          <!-- Conflit de professeur -->
                          <div v-else-if="coursDisponibilite[cours.tempId].professeurOccupe || coursDisponibilite[cours.tempId].conflictingCours?.conflictType === 'professeur'">
                            <strong>Professeur occupé :</strong>
                            <span v-if="coursDisponibilite[cours.tempId].conflictingCours?.details?.professeur">
                              <strong>{{ coursDisponibilite[cours.tempId].conflictingCours.details.professeur }}</strong>
                            </span>
                            est déjà occupé(e)
                            le <strong>{{ coursDisponibilite[cours.tempId].conflictingCours.details.jour }}</strong>
                            de <strong>{{ coursDisponibilite[cours.tempId].conflictingCours.details.heureDebut }}</strong>
                            à <strong>{{ coursDisponibilite[cours.tempId].conflictingCours.details.heureFin }}</strong>
                            <span v-if="coursDisponibilite[cours.tempId].conflictingCours.details.classe">
                              avec la classe <strong>{{ coursDisponibilite[cours.tempId].conflictingCours.details.classe }}</strong>
                            </span>
                          </div>
                          
                          <!-- Affichage générique -->
                          <div v-else>
                            <strong>Conflit détecté :</strong>
                            Ce créneau horaire n'est pas disponible
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- ===== FIN SECTION ALERTES ===== -->

                </div>
              </div>
              <hr class="mt-0" />
            </div>
          </div>
        </div>

        <!-- Boutons d'action -->
        <div class="row">
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit"
                :disabled="isSubmitting || hasAvailabilityConflicts"
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                {{ isSubmitting ? 'Création' : 'Ajouter l\'emploi du temps' }}
              </button>
              <router-link
                to="/liste-emplois"
                class="btn btn-danger transition border-0 lh-1 fw-medium"
              >
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i>
                Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed, watch } from "vue";
import Swal from 'sweetalert2';
import { Form, Field, ErrorMessage } from "vee-validate";
import { success, error } from "../../utils/utils";
import { useRouter } from "vue-router";
import ApiService from "../../services/ApiService";
import * as Yup from "yup";
import Multiselect from '@vueform/multiselect';

// Interfaces TypeScript
interface Option {
  value: string | number;
  label: string;
}

interface CoursForm {
  jour: string; // Changé de string[] à string
  heureDebut: string;
  heureFin: string;
  filiereNiveauMatiere: number | null;
  professeur: number | null;
  classe: number | null;
  estEnLigne: boolean;
  lienVisio?: string;
  matiereId: number | null;
  tempId: number;
}

interface Disponibilite {
  available: boolean;
  checking?: boolean;
  conflictingDays?: string[];
  classeId?: number;
  classeOccupee?: boolean;
  error?: boolean;
  professeurOccupe?: boolean;
  details?: string[];           
  conflictingCours?: any;
}

interface AvailabilityResponse {
  available: boolean;
  hasConflict: boolean;
  classeOccupee?: boolean;
  professeurOccupe?: boolean;
  conflictingCours: any | null;
  error?: boolean;
   details?: string[]; 
}

export default defineComponent({
  name: "AddEmploiDuTemps",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {
    // Schema de validation avec typage explicite
    const emploiDuTempsSchema = Yup.object().shape({
      typeEmploi: Yup.string().required("Le type d'emploi est obligatoire"),
      dateDebut: Yup.date()
        .required("La date de début est obligatoire")
        .min(new Date(new Date().setHours(0,0,0,0)), "La date de début ne peut pas être dans le passé"),
      dateFin: Yup.date()
        .required("La date fin est obligatoire")
        .min(
          Yup.ref("dateDebut"),
          "La date de fin doit être supérieure ou égale à la date de début"
        ),
      niveau: Yup.number().nullable().required("Le niveau est obligatoire"),
      filiere: Yup.number().nullable().required("La filière est obligatoire"),
      coursAdded: Yup.array().of(
      Yup.object().shape({
        jour: Yup.string().required("Le jour est obligatoire"),
        heureDebut: Yup.string().required("L'heure de début est obligatoire"),
        heureFin: Yup.string()
          .required("L'heure de fin est obligatoire")
          .test(
            "heure-fin-apres-debut",
            "L'heure de fin doit être supérieure à l'heure de début",
            function(value) {
              const { heureDebut } = this.parent;
              if (!heureDebut || !value) return true;
              return value > heureDebut;
            }
          ),
        filiereNiveauMatiere: Yup.number().nullable().required("La matière est obligatoire"),
        professeur: Yup.number().nullable().required("Le professeur est obligatoire"),
        classe: Yup.number().nullable().required("La classe est obligatoire"),
        estEnLigne: Yup.boolean(),
        lienVisio: Yup.string()
        .when('estEnLigne', {
          is: true,
          then: (schema) => schema
            .url("Le lien doit être une URL valide")
            .required("Le lien visio est obligatoire pour les cours en ligne"),
          otherwise: (schema) => schema.notRequired()
        })
      })
    ).min(1, "Veuillez ajouter au moins un cours"),
        });

    const emploiDuTempsForm = ref<any>(null);
    const router = useRouter();
    
    // Variables réactives
    const typeEmploiOptions = ref<Option[]>([]);
    const classesOptions = ref<Option[]>([]);
    const globalNiveauxOptions = ref<Option[]>([]);
    const globalFilieresOptions = ref<Option[]>([]);
    const globalMatieresOptions = ref<any[]>([]);
    const joursOptions = ref<Option[]>([
      { value: 'Lundi', label: 'Lundi' },
      { value: 'Mardi', label: 'Mardi' },
      { value: 'Mercredi', label: 'Mercredi' },
      { value: 'Jeudi', label: 'Jeudi' },
      { value: 'Vendredi', label: 'Vendredi' },
      { value: 'Samedi', label: 'Samedi' },
      { value: 'Dimanche', label: 'Dimanche' },
    ]);
    
    const globalSelectedNiveau = ref<number | null>(null);
    const globalSelectedFiliere = ref<number | null>(null);
    const professeursParMatiere = ref<Map<number, Option[]>>(new Map());
    const addedCours = ref<CoursForm[]>([]);
    const isSubmitting = ref(false);
    const isLoadingMatieres = ref(false);
    const isLoadingFilieres = ref(false);
    const coursDisponibilite = ref<Record<number, Disponibilite>>({});

    const typeEmploi = ref<any>(null);
    const dateDebut = ref<string | null>(null);
    const dateFin = ref<string | null>(null);

    const emploiExiste = ref(false);
    const existingEmploi = ref<any>(null);
    
    let tempCoursIdCounter = 1;

    function onVisioInput(event: Event, index: number) {
  const target = event.target as HTMLInputElement;
  if (addedCours[index]) {
    addedCours[index].lienVisio = target.value;
  }
}
    // Computed properties
    const hasAvailabilityConflicts = computed(() => {
      return Object.values(coursDisponibilite.value).some((dispo: Disponibilite) => !dispo.available);
    });

    // Nouvelle propriété calculée pour vérifier si le dernier cours est complet
    const isLastCoursComplete = computed(() => {
  if (addedCours.value.length === 0) {
    console.log('Aucun cours, bouton actif');
    return true; // Permet d'ajouter le premier cours
  }
  
  const lastCours = addedCours.value[addedCours.value.length - 1];
  if (!lastCours) {
    console.log('Dernier cours inexistant');
    return false;
  }

  const isComplete = (
    !!lastCours.jour && 
    !!lastCours.heureDebut &&
    !!lastCours.heureFin &&
    !!lastCours.filiereNiveauMatiere &&
    !!lastCours.professeur &&
    !!lastCours.classe
  );

  console.log('Vérification complétude:', {
    jour: !!lastCours.jour,
    heureDebut: !!lastCours.heureDebut,
    heureFin: !!lastCours.heureFin,
    matiere: !!lastCours.filiereNiveauMatiere,
    professeur: !!lastCours.professeur,
    classe: !!lastCours.classe,
    resultat: isComplete
  });

  return isComplete;
});

// Fonction pour calculer la différence en jours
const getDiffJours = (dateDebutStr: string, dateFinStr: string): number => {
  if (!dateDebutStr || !dateFinStr) return 0;
  
  const debut = new Date(dateDebutStr);
  const fin = new Date(dateFinStr);
  
  const diffTime = fin.getTime() - debut.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 pour inclure le jour de début
  
  return diffDays;
};

// Fonction pour calculer la date de fin en fonction du type et de la date de début
const calculateDateFin = (typeEmploiLibelle: string, dateDebutStr: string): string => {
  if (!typeEmploiLibelle || !dateDebutStr) return dateDebutStr;
  
  const dateDebut = new Date(dateDebutStr);
  let dateFin = new Date(dateDebut);
  
  const typeNormalized = typeEmploiLibelle.toLowerCase().trim();
  
  switch(typeNormalized) {
    case 'hebdomadaire':
      // 7 jours (inclus le premier jour, donc +6 jours)
      dateFin.setDate(dateDebut.getDate() + 6);
      break;
      
    case 'mensuel':
    case 'mensuelle':
      // 1 mois (28-31 jours selon le mois)
      dateFin.setMonth(dateDebut.getMonth() + 1);
      dateFin.setDate(dateFin.getDate() - 1);
      break;
      
    case 'trimestriel':
    case 'trimestrielle':
      // 3 mois (environ 90 jours)
      dateFin.setMonth(dateDebut.getMonth() + 3);
      dateFin.setDate(dateFin.getDate() - 1);
      break;
      
    case 'semestriel':
    case 'semestrielle':
      // 6 mois (environ 180 jours)
      dateFin.setMonth(dateDebut.getMonth() + 6);
      dateFin.setDate(dateFin.getDate() - 1);
      break;
      
    case 'annuel':
    case 'annuelle':
      // 1 an (365 jours)
      dateFin.setFullYear(dateDebut.getFullYear() + 1);
      dateFin.setDate(dateFin.getDate() - 1);
      break;
      
    default:
      // Par défaut, retourne la même date
      return dateDebutStr;
  }
  
  // Formater la date au format YYYY-MM-DD
  const year = dateFin.getFullYear();
  const month = String(dateFin.getMonth() + 1).padStart(2, '0');
  const day = String(dateFin.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// Ref pour savoir si la date de fin est calculée automatiquement
const isDateFinAutoCalculated = ref(true);
const selectedTypeEmploiLibelle = ref<string>('');

// Watcher pour le type d'emploi du temps
watch(
  () => typeEmploi.value,
  (newTypeEmploi) => {
    if (newTypeEmploi) {
      // Récupérer le libellé du type sélectionné
      const selectedType = typeEmploiOptions.value.find(
        (opt) => opt.value === (typeof newTypeEmploi === 'object' ? newTypeEmploi.value : newTypeEmploi)
      );
      
      if (selectedType) {
        selectedTypeEmploiLibelle.value = selectedType.label;
        
        // Si on a déjà une date de début, recalculer la date de fin
        if (dateDebut.value) {
          const newDateFin = calculateDateFin(selectedType.label, dateDebut.value);
          dateFin.value = newDateFin;
          emploiDuTempsForm.value?.setFieldValue('dateFin', newDateFin);
        }
      }
    }
  }
);

// Watcher pour la date de début
watch(
  () => dateDebut.value,
  (newDateDebut) => {
    if (newDateDebut && selectedTypeEmploiLibelle.value) {
      const newDateFin = calculateDateFin(selectedTypeEmploiLibelle.value, newDateDebut);
      dateFin.value = newDateFin;
      emploiDuTempsForm.value?.setFieldValue('dateFin', newDateFin);
    }
  }
);

// Modifier la fonction onTypeEmploiChange (à ajouter si elle n'existe pas)
const onTypeEmploiChange = (typeValue: any) => {
  // Vérifie que la liste d'options existe
  if (!typeEmploiOptions?.value || !Array.isArray(typeEmploiOptions.value)) {
    console.warn("typeEmploiOptions n'est pas encore disponible");
    return;
  }

  // Vérifie que la valeur est valide
  if (!typeValue) {
    console.warn("typeValue est nul ou vide");
    return;
  }

  typeEmploi.value = typeValue;

  // Trouve le type sélectionné en toute sécurité
  const selectedType = typeEmploiOptions.value.find(
    (opt) => opt.value === (typeof typeValue === 'object' ? typeValue.value : typeValue)
  );

  if (selectedType) {
    selectedTypeEmploiLibelle.value = selectedType.label;

    if (dateDebut.value) {
      const newDateFin = calculateDateFin(selectedType.label, dateDebut.value);
      dateFin.value = newDateFin;
      emploiDuTempsForm.value?.setFieldValue('dateFin', newDateFin);
    }
  }
};

// Modifier la fonction onDateDebutChange (à ajouter)
const onDateDebutChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newDateDebut = target.value;
  dateDebut.value = newDateDebut;
  
  if (newDateDebut && selectedTypeEmploiLibelle.value) {
    const newDateFin = calculateDateFin(selectedTypeEmploiLibelle.value, newDateDebut);
    dateFin.value = newDateFin;
    emploiDuTempsForm.value?.setFieldValue('dateFin', newDateFin);
  }
};

    // Fonctions utilitaires
    const getProfesseursByMatiere = (matiereId: number | null): Option[] => {
      if (!matiereId || typeof matiereId !== 'number' || matiereId <= 0) {
        return [];
      }
      return professeursParMatiere.value.get(matiereId) || [];
    };

    const getClasseLabel = (classeId: number | null): string => {
      if (!classeId || classeId === null) {
        return 'inconnue';
      }
      const classe = classesOptions.value.find(c => c.value === classeId);
      return classe ? classe.label : `ID ${classeId}`;
    };

    const handleInvalidSubmit = ({ errors }: any) => {
      console.error("Soumission invalide, erreurs :", JSON.stringify(errors, null, 2));
      error("Veuillez remplir les champs du formulaire");
    };

    const getCurrentDate = (): string => {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      return `${year}-${month}-${day}`;
    };

    // Fonctions de validation
    // const checkDuplicateMatieres = (coursAdded: CoursForm[]): string[] => {
    //   const errors: string[] = [];
    //   const matieresSeen = new Set<number>();
      
    //   coursAdded.forEach((cours, index) => {
    //     if (cours.filiereNiveauMatiere && matieresSeen.has(cours.filiereNiveauMatiere)) {
    //       const matiere = globalMatieresOptions.value.find(m => m.value === cours.filiereNiveauMatiere);
    //       errors.push(`Cours ${index + 1}: La matière "${matiere?.label}" est déjà présente dans cet emploi du temps`);
    //     }
        
    //     if (cours.filiereNiveauMatiere) {
    //       matieresSeen.add(cours.filiereNiveauMatiere);
    //     }
    //   });
      
    //   return errors;
    // };

    const checkEmploiDuTempsExist = async () => {
  if (!typeEmploi.value || !globalSelectedFiliere.value || !globalSelectedNiveau.value || !dateDebut.value || !dateFin.value) {
    return;
  }

  try {
    const response = await ApiService.query('/emploi-du-temps/check-exist', {
      params: {
        typeEmploiId: typeEmploi.value.id || typeEmploi.value,
        filiereId: globalSelectedFiliere.value,
        niveauId: globalSelectedNiveau.value,
        dateDebut: dateDebut.value,
        dateFin: dateFin.value
      }
    });

    emploiExiste.value = response.data.exists;
    existingEmploi.value = response.data.emploiDuTemps;

    if (emploiExiste.value) {
      Swal.fire({
        icon: 'warning',
        title: 'Emploi du temps existant',
        html: `
          Un emploi du temps existe déjà pour cette période, filière et niveau.<br/>
          Voulez-vous le mettre à jour pour ajouter de nouveaux cours ?
        `,
        showCancelButton: true,
        confirmButtonText: 'Mettre à jour',
        cancelButtonText: 'Annuler'
      }).then((result) => {
        if (result.isConfirmed && existingEmploi.value) {
          // Ici, tu peux naviguer vers la page de mise à jour avec l'ID de l'emploi du temps
          // Exemple avec Vue Router :
          // router.push({ name: 'EditEmploiDuTemps', params: { id: existingEmploi.value.id } });
        }
      });
    }

  } catch (error) {
    console.error('Erreur vérification emploi du temps:', error);
  }
};

    const checkDateConsistency = (dateDebut: string, dateFin: string): string[] => {
      const errors: string[] = [];
      const debut = new Date(dateDebut);
      const fin = new Date(dateFin);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (debut < today) {
        errors.push("La date de début ne peut pas être dans le passé");
      }
      
      if (fin < debut) {
        errors.push("La date de fin doit être supérieure ou égale à la date de début");
      }
      
      const diffMonths = (fin.getTime() - debut.getTime()) / (1000 * 60 * 60 * 24 * 30);
      if (diffMonths > 12) {
        errors.push("La durée de l'emploi du temps ne peut pas dépasser 12 mois");
      }
      
      return errors;
    };

    const checkTimeOverlaps = (coursAdded: CoursForm[]): string[] => {
      const errors: string[] = [];
      
      coursAdded.forEach((cours1, index1) => {
        if (cours1.estEnLigne) return;
        
        coursAdded.forEach((cours2, index2) => {
          if (index2 <= index1 || cours2.estEnLigne) return;
          
          if (cours2.jour === cours1.jour) { // Changé de includes à égalité stricte
            if (!(cours1.heureFin <= cours2.heureDebut || cours1.heureDebut >= cours2.heureFin)) {
              errors.push(
                `Chevauchement horaire détecté le ${cours1.jour} entre le cours ${index1 + 1} ` +
                `(${cours1.heureDebut}-${cours1.heureFin}) et le cours ${index2 + 1} ` +
                `(${cours2.heureDebut}-${cours2.heureFin})`
              );
            }
          }
        });
      });
      
      return errors;
    };

    const checkWorkload = (coursAdded: CoursForm[]): string[] => {
      const errors: string[] = [];
      const joursCharges: Record<string, number> = {};
      
      coursAdded.forEach((cours, index) => {
        if (cours.estEnLigne || !cours.heureDebut || !cours.heureFin) return;
        
        const [debutH, debutM] = cours.heureDebut.split(':').map(Number);
        const [finH, finM] = cours.heureFin.split(':').map(Number);
        const dureeMinutes = (finH * 60 + finM) - (debutH * 60 + debutM);
        
        if (dureeMinutes < 30) {
          errors.push(`Cours ${index + 1}: Durée trop courte (minimum 30 minutes)`);
        }
        
        if (dureeMinutes > 480) {
          errors.push(`Cours ${index + 1}: Durée trop longue (maximum 8 heures)`);
        }
        
        joursCharges[cours.jour] = (joursCharges[cours.jour] || 0) + dureeMinutes; // Changé de boucle à accès direct
      });
      
      Object.entries(joursCharges).forEach(([jour, totalMinutes]) => {
        if (totalMinutes > 720) {
          errors.push(`Charge horaire trop élevée le ${jour}: ${Math.round(totalMinutes/60)}h (maximum 12h)`);
        }
      });
      
      return errors;
    };

    const checkAvailabilityWithDates = async (
      classeId: number,
      jour: string,
      heureDebut: string,
      heureFin: string,
      dateDebut: string,
      dateFin: string,
      estEnLigne?: boolean,
      professeurId?: number
    ): Promise<AvailabilityResponse> => {
      try {
        if (!classeId || !jour || !heureDebut || !heureFin || !dateDebut || !dateFin) {
          console.error('Paramètres obligatoires manquants');
          return { available: false, hasConflict: true, conflictingCours: null, error: true };
        }
        
        const cleanJour = jour.toString().trim();
        if (!cleanJour || cleanJour === 'undefined') {
          console.error('Jour invalide après nettoyage:', jour);
          return { available: false, hasConflict: true, conflictingCours: null, error: true };
        }
        
        let url = `/check-classe-availability?classeId=${classeId}&jour=${encodeURIComponent(cleanJour)}&heureDebut=${heureDebut}&heureFin=${heureFin}&dateDebut=${dateDebut}&dateFin=${dateFin}`;
        
        if (professeurId && professeurId > 0) {
          url += `&professeurId=${professeurId}`;
        }
        
        if (estEnLigne !== undefined) {
          url += `&estEnLigne=${estEnLigne}`;
        }
        
        console.log('URL de vérification avec dates:', url);
        
        const response = await ApiService.get(url);
        console.log('Résultat vérification avec dates:', response.data.data);
        
        return response.data.data as AvailabilityResponse;
      } catch (err: any) {
        console.error("Erreur lors de la vérification de disponibilité avec dates", err);
        return { available: false, hasConflict: true, conflictingCours: null, error: true };
      }
    };

    // Fonctions d'initialisation
    onMounted(() => {
      fetchTypeEmploi();
      fetchClasses();
      fetchNiveaux();
      addNewCours();
      fetchProfesseursParMatiere();
    });

    const fetchTypeEmploi = async () => {
      try {
        const response = await ApiService.get('/typeEmploiDuTemps?limit=0');
        const typeEmploiData = response.data.data.data;
        typeEmploiOptions.value = typeEmploiData.map((type: any) => ({
          value: type.id,
          label: type.libelle,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des types d'emploi", err);
        error("Impossible de charger les types d'emploi");
      }
    };

    const fetchClasses = async () => {
      try {
        const response = await ApiService.get('/all/classes?limit=0');
        const classesData = response.data.data.data;
        classesOptions.value = classesData.map((classe: any) => ({
          value: classe.id,
          label: classe.libelle,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des classes", err);
      }
    };

    const fetchProfesseursParMatiere = async () => {
      try {
        console.log('Début du chargement des professeurs par matière...');
        
        const responseMatieres = await ApiService.get('/filiereNiveauMatieres?limit=0');
        
        if (responseMatieres.data?.code === 200 && Array.isArray(responseMatieres.data.data?.data)) {
          const toutesLesMatieres = responseMatieres.data.data.data;
          console.log(`${toutesLesMatieres.length} filiereNiveauMatieres trouvées`);
          
          const matiereIdsSet = new Set<number>();
          toutesLesMatieres.forEach((fnm: any) => {
            if (fnm?.matiere?.id && typeof fnm.matiere.id === 'number' && fnm.matiere.id > 0) {
              matiereIdsSet.add(fnm.matiere.id);
            }
          });
          
          const matiereIds: number[] = Array.from(matiereIdsSet);
          console.log(`${matiereIds.length} matières uniques trouvées:`, matiereIds);

          if (matiereIds.length === 0) {
            console.warn('Aucune matière valide trouvée');
            return;
          }

          const professeurPromises = matiereIds.map(async (matiereId: number) => {
            try {
              console.log(`Chargement des professeurs pour la matière ${matiereId}...`);
              const responseProfesseurs = await ApiService.get(`/professeurs/matiere/${matiereId}?limit=0`);
              
              if (responseProfesseurs.data?.code === 200 && Array.isArray(responseProfesseurs.data.data?.data)) {
                const professeursData = responseProfesseurs.data.data.data;
                const professeursOptions = professeursData
                  .map((prof: any) => ({
                    value: Number(prof.id),
                    label: `${prof.nom || ''} ${prof.prenom || ''}`.trim(),
                    matiereId: matiereId,
                    email: prof.email,
                    npi: prof.npi
                  }))
                  .filter((prof: any) => prof.value > 0 && prof.label && prof.label !== '');
                
                professeursParMatiere.value.set(matiereId, professeursOptions);
                console.log(`✓ Matière ${matiereId}: ${professeursOptions.length} professeurs chargés`);
                return { matiereId, success: true, count: professeursOptions.length };
              } else {
                console.warn(`Réponse API invalide pour matière ${matiereId}:`, responseProfesseurs.data);
                professeursParMatiere.value.set(matiereId, []);
                return { matiereId, success: false, error: 'API response invalid' };
              }
            } catch (err: any) {
              console.error(`Erreur pour la matière ${matiereId}:`, err.message);
              
              if (err.response?.status === 404) {
                console.log(`Aucun professeur trouvé pour la matière ${matiereId}`);
                professeursParMatiere.value.set(matiereId, []);
                return { matiereId, success: true, count: 0, empty: true };
              }
              
              professeursParMatiere.value.set(matiereId, []);
              return { matiereId, success: false, error: err.message };
            }
          });

          const results = await Promise.all(professeurPromises);
          const successfulLoads = results.filter(r => r.success);
          const failedLoads = results.filter(r => !r.success);
          const emptyLoads = results.filter(r => r.empty);
          
          console.log(`Chargement terminé: ${successfulLoads.length} succès, ${emptyLoads.length} vides, ${failedLoads.length} échecs`);
          console.log('Map des professeurs:', professeursParMatiere.value);
          
        } else {
          console.warn('Réponse API matières invalide:', responseMatieres.data);
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des professeurs par matière", err);
      }
    };

    const fetchNiveaux = async () => {
      try {
        const response = await ApiService.get('/all/niveaux?limit=0');
        const niveauxData = response.data.data.data;
        globalNiveauxOptions.value = niveauxData.map((niveau: any) => ({
          value: niveau.id,
          label: niveau.libelle,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des niveaux", err);
        error("Impossible de charger les niveaux");
      }
    };

    const fetchFilieresParNiveau = async (niveauId: number) => {
      if (!niveauId) {
        globalFilieresOptions.value = [];
        error("Veuillez sélectionner un niveau pour charger les filières.");
        return;
      }

      isLoadingFilieres.value = true;
      try {
        const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&limit=0`);
        console.log('Réponse API filières par niveau:', response.data);
        if (response.data.code === 200) {
          const filiereNiveauMatieres = response.data.data.data;
          if (filiereNiveauMatieres.length === 0) {
            error("Aucune filière trouvée pour ce niveau.");
            globalFilieresOptions.value = [];
            return;
          }
          const filieresUniques = Array.from(new Set(filiereNiveauMatieres.map((fnm: any) => fnm.filiere.id)))
            .map(id => filiereNiveauMatieres.find((fnm: any) => fnm.filiere.id === id).filiere);
          globalFilieresOptions.value = filieresUniques.map((filiere: any) => ({
            value: filiere.id,
            label: filiere.libelle,
          }));
        } else {
          globalFilieresOptions.value = [];
          error("Erreur lors de la récupération des filières.");
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des filières filtrées", err);
        globalFilieresOptions.value = [];
        error("Impossible de charger les filières pour ce niveau.");
      } finally {
        isLoadingFilieres.value = false;
      }
    };

    const fetchMatieresFiltered = async (niveauId: number, filiereId: number) => {
      if (!niveauId || !filiereId) {
        globalMatieresOptions.value = [];
        error("Veuillez sélectionner un niveau et une filière pour charger les matières.");
        return;
      }

      isLoadingMatieres.value = true;
      try {
        const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&filiere=${filiereId}&limit=0`);
        console.log('Réponse API matières:', response.data);
        if (response.data.code === 200) {
          const filiereNiveauMatieres = response.data.data.data;
          if (filiereNiveauMatieres.length === 0) {
            error("Aucune matière trouvée pour cette combinaison niveau-filière.");
            globalMatieresOptions.value = [];
            return;
          }
          globalMatieresOptions.value = filiereNiveauMatieres.map((fnm: any) => ({
            value: fnm.id,
            label: `${fnm.matiere.libelle}`,
            coefficient: fnm.coefficient,
            matiereId: fnm.matiere.id,
            matiere: fnm.matiere,
            niveauId: fnm.niveau.id,
            niveau: fnm.niveau,
            filiereId: fnm.filiere.id
          }));
          console.log('Matières avec coefficients:', globalMatieresOptions.value);
        } else {
          globalMatieresOptions.value = [];
          error("Erreur lors de la récupération des matières.");
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des matières", err);
        globalMatieresOptions.value = [];
        error("Impossible de charger les matières.");
      } finally {
        isLoadingMatieres.value = false;
      }
    };

    // Gestionnaires d'événements
    const onGlobalNiveauChange = async (niveauValue: any) => {
      globalSelectedNiveau.value = typeof niveauValue === 'object' ? niveauValue.value : niveauValue;
      console.log('Niveau sélectionné (ID):', globalSelectedNiveau.value);

      globalSelectedFiliere.value = null;
      globalFilieresOptions.value = [];
      globalMatieresOptions.value = [];
      await emploiDuTempsForm.value?.setFieldValue(`filiere`, null);

      if (globalSelectedNiveau.value) {
        await fetchFilieresParNiveau(globalSelectedNiveau.value);
      }
    };

    const onGlobalFiliereChange = async (filiereValue: any) => {
      globalSelectedFiliere.value = filiereValue;
      console.log('Filière sélectionnée:', filiereValue);
      
      if (filiereValue && globalSelectedNiveau.value) {
        await fetchMatieresFiltered(globalSelectedNiveau.value, filiereValue);
      }
    };

    const onMatiereChange = async (index: number, matiereValue: any) => {
      console.log(`Changement de matière pour le cours ${index}:`, matiereValue);

      if (!addedCours.value[index]) return;

      const selectedOption = globalMatieresOptions.value.find(opt => opt.value === matiereValue);

      addedCours.value[index].filiereNiveauMatiere = matiereValue;
      addedCours.value[index].matiereId = selectedOption?.matiereId ?? null;

      addedCours.value[index].professeur = null;
    };

    const onJourChange = (index: number, jour: string | Option) => {
      console.log(`Changement de jour pour le cours ${index}:`, jour);
      if (addedCours.value[index]) {
        if (typeof jour === 'object' && jour !== null && 'value' in jour) {
          addedCours.value[index].jour = jour.value as string;
        } else {
          addedCours.value[index].jour = jour as string;
        }
        
        console.log(`Jour mis à jour pour le cours ${index}:`, addedCours.value[index].jour);
        checkCoursAvailability(index);
      }
    };

    const onHeureChange = (index: number, type: 'debut' | 'fin', event: Event) => {
      const target = event.target as HTMLInputElement;
      const value = target.value;
      
      console.log(`Changement d'heure ${type} pour le cours ${index}:`, value);
      if (addedCours.value[index]) {
        if (type === 'debut') {
          addedCours.value[index].heureDebut = value;
        } else {
          addedCours.value[index].heureFin = value;
        }
        checkCoursAvailability(index);
      }
    };

    const onClasseChange = (index: number, classeValue: Option | number | null) => {
      console.log(`Changement de classe pour le cours ${index}:`, classeValue);
      
      if (!addedCours.value[index]) return;
      
      if (classeValue === null) {
        addedCours.value[index].classe = null;
        console.log(`Classe réinitialisée pour le cours ${index}`);
        checkCoursAvailability(index);
        return;
      }
      
      let classeId: number | null = null;
      
      if (typeof classeValue === 'number') {
        classeId = classeValue;
      } else if (typeof classeValue === 'object' && classeValue !== null && 'value' in classeValue) {
        classeId = (classeValue as Option).value as number;
      }
      
      addedCours.value[index].classe = classeId;
      console.log(`Classe définie pour le cours ${index}:`, classeId);
      
      if (classeId !== null) {
        checkCoursAvailability(index);
      }
    };

    const addNewCours = () => {
      const newCours: CoursForm = {
        jour: "", // Initialisé comme string vide
        heureDebut: "",
        heureFin: "",
        filiereNiveauMatiere: null,
        professeur: null,
        classe: null,
        estEnLigne: false,
        // lienVisio: null, 
        matiereId: null,
        tempId: tempCoursIdCounter++,
      };
      addedCours.value.push(newCours);
      coursDisponibilite.value[newCours.tempId] = { available: true, checking: false };
    };

    const removeCours = (index: number) => {
      const coursToRemove = addedCours.value[index];
      addedCours.value.splice(index, 1);
      delete coursDisponibilite.value[coursToRemove.tempId];
    };

    const handleCheckboxChange = (index: number, event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;
  addedCours.value[index].estEnLigne = checked;
  
  // Réinitialise le lien visio si le cours n'est plus en ligne
  if (!checked) {
    delete addedCours.value[index].lienVisio; 
    emploiDuTempsForm.value?.setFieldValue(`coursAdded[${index}].lienVisio`, '');
  }
  
  if (checked) {
    const tempId = addedCours.value[index].tempId;
    coursDisponibilite.value[tempId] = { available: true, checking: false };
  } else {
    checkCoursAvailability(index);
  }
};
    const checkAvailability = async (
      classeId: number, 
      jour: string, 
      heureDebut: string, 
      heureFin: string,
      professeurId?: number
    ): Promise<AvailabilityResponse> => {
      try {
        if (!classeId || !jour || !heureDebut || !heureFin) {
          console.error('Paramètres invalides:', { classeId, professeurId, jour, heureDebut, heureFin });
          return { available: false, hasConflict: true, conflictingCours: null, error: true };
        }
        
        const cleanJour = jour.toString().trim();
        if (!cleanJour || cleanJour === 'undefined') {
          console.error('Jour invalide après nettoyage:', jour);
          return { available: false, hasConflict: true, conflictingCours: null, error: true };
        }
        
        console.log('Vérification disponibilité - classe:', classeId, 'jour:', cleanJour, 'heures:', heureDebut, '-', heureFin);
        console.log('Vérification disponibilité - professeur:', professeurId, 'jour:', cleanJour, 'heures:', heureDebut, '-', heureFin);
        
        let url = `/check-classe-availability?classeId=${classeId}&jour=${encodeURIComponent(cleanJour)}&heureDebut=${heureDebut}&heureFin=${heureFin}`;
        
        if (professeurId && professeurId > 0) {
          url += `&professeurId=${professeurId}`;
        }
        
        const response = await ApiService.get(url);
        
        console.log('Résultat vérification:', response.data.data);
        return response.data.data as AvailabilityResponse;
      } catch (err: any) {
        console.error("Erreur lors de la vérification de disponibilité", err);
        return { available: false, hasConflict: true, conflictingCours: null, error: true };
      }
    };

    const checkLiveAvailability = async (
  classeId: number,
  jour: string,
  heureDebut: string,
  heureFin: string,
  professeurId?: number,
  estEnLigne?: boolean
): Promise<AvailabilityResponse> => {
  try {
    let url = `/check-classe-availability?classeId=${classeId}&jour=${encodeURIComponent(jour)}&heureDebut=${heureDebut}&heureFin=${heureFin}`;

    if (professeurId && professeurId > 0) {
      url += `&professeurId=${professeurId}`;
    }

    if (estEnLigne !== undefined) {
      url += `&estEnLigne=${estEnLigne}`;
    }

    const response = await ApiService.get(url);
    return response.data.data as AvailabilityResponse;
  } catch (err: any) {
    console.error("Erreur live check", err);
    return { available: false, hasConflict: true, conflictingCours: null, error: true };
  }
};

  const checkCoursAvailability = async (index: number) => {
  const cours = addedCours.value[index];

  if (!cours || cours.estEnLigne || !cours.jour || !cours.classe || !cours.heureDebut || !cours.heureFin) {
    const tempId = cours?.tempId;
    if (tempId) {
      coursDisponibilite.value[tempId] = { available: true, checking: false };
    }
    return;
  }

  const tempId = cours.tempId;
  coursDisponibilite.value[tempId] = { ...coursDisponibilite.value[tempId], checking: true };

  try {
    const availability = await checkLiveAvailability(
      cours.classe as number,
      cours.jour,
      cours.heureDebut,
      cours.heureFin,
      cours.professeur || undefined,
      cours.estEnLigne
    );

    coursDisponibilite.value[tempId] = {
      available: availability.available, 
      checking: false,
      professeurOccupe: availability.professeurOccupe,
      classeOccupee: availability.classeOccupee, 
      conflictingCours: availability.conflictingCours 
    };
  } catch (err) {
    coursDisponibilite.value[tempId] = {
      available: true,
      checking: false,
      error: true
    };
  }
};

 const checkSameMatiereDiffProf = (coursList: any[]) => {
  const matiereProfMap = new Map<string, any>();

  for (let i = 0; i < coursList.length; i++) {
    const cours = coursList[i];

    if (!cours.filiereNiveauMatiere || !cours.professeur) continue;

    const matiereId = typeof cours.filiereNiveauMatiere === 'object' 
      ? cours.filiereNiveauMatiere.value || cours.filiereNiveauMatiere.id 
      : cours.filiereNiveauMatiere;

    const profId = typeof cours.professeur === 'object' 
      ? cours.professeur.value || cours.professeur.id 
      : cours.professeur;

    const existingProf = matiereProfMap.get(matiereId);

    if (existingProf && existingProf !== profId) {
      return {
        error: true,
        message: `La matière "${cours.filiereNiveauMatiere.label || cours.filiereNiveauMatiere.libelle || matiereId}" est déjà attribuée à un autre professeur dans cette classe.`,
      };
    }

    matiereProfMap.set(matiereId, profId);
  }

  return { error: false };
};

    const addEmploiDuTemps = async (values: any, { resetForm }: any) => {
      isSubmitting.value = true;
      
      try {
        console.log('=== DÉBUT DES VÉRIFICATIONS COMPLÈTES ===');
        
        // const duplicateErrors = checkDuplicateMatieres(values.coursAdded);
        // if (duplicateErrors.length > 0) {
        //   error(`Matières en double détectées :\n${duplicateErrors.join('\n')}`);
        //   return;
        // }
        console.log('✓ Pas de matières en double');
        
        const dateErrors = checkDateConsistency(values.dateDebut, values.dateFin);
        if (dateErrors.length > 0) {
          error(`Erreurs de dates :\n${dateErrors.join('\n')}`);
          return;
        }
        console.log('✓ Dates cohérentes');
        
        const overlapErrors = checkTimeOverlaps(values.coursAdded);
        if (overlapErrors.length > 0) {
          error(`Conflits horaires :\n${overlapErrors.join('\n')}`);
          return;
        }
        console.log('✓ Pas de chevauchements horaires');

        const sameMatiereCheck = checkSameMatiereDiffProf(values.coursAdded);
          if (sameMatiereCheck.error) {
            error(sameMatiereCheck.message);
            return;
          }
          console.log('✓ Pas de même matière attribuée à plusieurs professeurs');

        
        const workloadErrors = checkWorkload(values.coursAdded);
        if (workloadErrors.length > 0) {
          error(`Problèmes de charge horaire :\n${workloadErrors.join('\n')}`);
          return;
        }
        console.log('✓ Charge de travail acceptable');
        
        console.log('Vérification finale de disponibilité avec dates...');
        let finalAvailabilityCheck = true;
        const finalConflicts: string[] = [];

        for (let i = 0; i < values.coursAdded.length; i++) {
          const cours = values.coursAdded[i];
          if (cours.estEnLigne) continue;

          if (!cours.jour || !cours.classe || !cours.heureDebut || !cours.heureFin) {
            finalConflicts.push(`Cours ${i + 1} : Données incomplètes`);
            finalAvailabilityCheck = false;
            continue;
          }

          const availability = await checkAvailabilityWithDates(
            cours.classe,
            cours.jour, // Jour est maintenant une string
            cours.heureDebut,
            cours.heureFin,
            values.dateDebut,
            values.dateFin,
            cours.professeur
          );
          
          if (!availability.available) {
            let conflictMessage = `Cours ${i + 1} : Conflit le ${cours.jour} de ${cours.heureDebut} à ${cours.heureFin}`;
            
            if (availability.classeOccupee) {
              conflictMessage += ` - Classe ${getClasseLabel(cours.classe)} occupée`;
            }
            
            if (availability.professeurOccupe) {
              const profName = availability.conflictingCours?.conflictDetails?.professeur || 'inconnu';
              conflictMessage += ` - Professeur ${profName} occupé`;
            }
            
            finalConflicts.push(conflictMessage);
            finalAvailabilityCheck = false;
          }
        }

        if (!finalAvailabilityCheck) {
          error(`Conflits de disponibilité détectés :\n${finalConflicts.join('\n')}`);
          return;
        }
        console.log('✓ Toutes les vérifications réussies');

        const payload: any = {
          typeEmploi: values.typeEmploi,
          dateDebut: values.dateDebut,
          dateFin: values.dateFin,
          filiereId: typeof values.filiere === 'object' ? values.filiere.value : values.filiere,
          niveauId: typeof values.niveau === 'object' ? values.niveau.value : values.niveau,
          cours: values.coursAdded.map((cours: any) => ({ // Changé de flatMap à map
            jour: cours.jour, // Jour est maintenant une string
            heureDebut: cours.heureDebut,
            heureFin: cours.heureFin,
            filiereNiveauMatiereId: cours.filiereNiveauMatiere,
            professeurId: cours.professeur,
            classeId: cours.classe,
            estEnLigne: cours.estEnLigne === true,
             lienVisio: cours.estEnLigne && cours.lienVisio ? cours.lienVisio : undefined
          })),
        };

        console.log("Payload final envoyé :", payload);
        const response = await ApiService.post('/emplois-du-temps', payload);
        
        // Vérifie le code HTTP réel
          if (response.status === 201 || response.status === 200) {
            success(response.data.message || "Emploi du temps créé avec succès !");
            resetForm();
            addedCours.value = [];
            globalSelectedNiveau.value = null;
            globalSelectedFiliere.value = null;
            globalFilieresOptions.value = [];
            globalMatieresOptions.value = [];
            coursDisponibilite.value = {};
            router.push('/liste-emplois');
          } else {
            throw new Error(response.data?.message || "Erreur inconnue du serveur");
          }
        
      } catch (err: any) {
        console.error("Erreur lors de l'ajout de l'emploi du temps:", err);
        if (err.response?.data?.message) {
          error(`${err.response.data.message}`);
        } else if (err.message) {
          error(err.message);
        } else {
          error("Impossible d'ajouter l'emploi du temps");
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    const debugCoursState = (index: number) => {
      const cours = addedCours.value[index];
      console.log(`=== DEBUG COURS ${index} ===`);
      console.log('Cours complet:', JSON.stringify(cours, null, 2));
      console.log('Jour type:', typeof cours?.jour);
      console.log('Jour contenu:', cours?.jour);
      console.log('Classe:', cours?.classe);
      console.log('Est en ligne:', cours?.estEnLigne);
      console.log('=========================');
    };

    watch(
  () => addedCours.value,
  (newCours) => {
    if (newCours.length > 0) {
      const lastCours = newCours[newCours.length - 1];
      console.log('=== État du dernier cours ===');
      console.log('Jour:', lastCours.jour);
      console.log('Heure début:', lastCours.heureDebut);
      console.log('Heure fin:', lastCours.heureFin);
      console.log('Matière:', lastCours.filiereNiveauMatiere);
      console.log('Professeur:', lastCours.professeur);
      console.log('Classe:', lastCours.classe);
      console.log('Est complet?', isLastCoursComplete.value);
      console.log('===========================');
    }
  },
  { deep: true }
);

watch(
  [typeEmploi, globalSelectedFiliere, globalSelectedNiveau, dateDebut, dateFin],
  checkEmploiDuTempsExist
);



    return {
      emploiDuTempsSchema,
      emploiDuTempsForm,
      typeEmploiOptions,
      classesOptions,
      globalNiveauxOptions,
      globalFilieresOptions,
      globalMatieresOptions,
      joursOptions,
      globalSelectedNiveau,
      globalSelectedFiliere,
      addedCours,
      isSubmitting,
      isLoadingMatieres,
      isLoadingFilieres,
      coursDisponibilite,
      hasAvailabilityConflicts,
      isLastCoursComplete, 
      getCurrentDate,
      getProfesseursByMatiere,
      getClasseLabel,
      handleInvalidSubmit,
      debugCoursState,
      addNewCours,
      removeCours,
      handleCheckboxChange,
      onGlobalNiveauChange,
      onGlobalFiliereChange,
      checkSameMatiereDiffProf,
      onMatiereChange,
      onJourChange,
      onHeureChange,
      onClasseChange,
      checkEmploiDuTempsExist,
      checkDateConsistency,
      checkTimeOverlaps,
      checkWorkload,
      checkAvailabilityWithDates,
      checkLiveAvailability,
      checkCoursAvailability,
      addEmploiDuTemps,
      calculateDateFin,
      isDateFinAutoCalculated,
      selectedTypeEmploiLibelle,
      onTypeEmploiChange,
      onDateDebutChange,
      typeEmploi,
      dateDebut,
      dateFin,
      getDiffJours,
      onVisioInput,
    };
  },
});
</script>


<style scoped>
@import '@vueform/multiselect/themes/default.css';

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-check-input:checked {
  background-color: #28a745;
  border-color: #28a745;
}

.alert {
  border-radius: 0.5rem;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.text-primary {
  color: #007bff !important;
}

.text-success {
  color: #28a745 !important;
}

.border-primary {
  border-color: #007bff !important;
}

.border-success {
  border-color: #28a745 !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.form-check-input.me-2 {
  margin-right: 0.75rem;
}

.badge {
  padding: 0.5em 0.75em;
  font-size: 0.875em;
}

.badge.bg-primary {
  background-color: #007bff !important;
}

.badge.bg-success {
  background-color: #28a745 !important;
}

.mt-30 {
  margin-top: 30px;
}

.row.mt-10 {
  margin-top: 10px;
}

.alert {
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
}

.alert-success {
  background-color: #d4edda;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.marge-droite {
  padding-right: 15px;
  padding-left: 15px;
}

.alert {
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
}

.alert-success {
  background-color: #d4edda;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.marge-droite {
  padding-right: 15px;
  padding-left: 15px;
}

.form-control:disabled,
.form-control[readonly] {
  background-color: #e9ecef;
  opacity: 0.7;
  cursor: not-allowed;
}

.form-control.bg-light:disabled {
  background-color: #f8f9fa !important;
}

/* Badge info pour indiquer le calcul automatique */
.badge.bg-info {
  background-color: #17a2b8 !important;
  font-size: 0.7rem;
  font-weight: normal;
  padding: 0.25rem 0.5rem;
}

small.text-muted {
  font-size: 0.8rem;
  font-style: italic;
}

small.text-muted i {
  color: #17a2b8;
}


</style>